import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { Option } from "components/MuiAsyncAutocomplete"
import { INITIAL_MORE_FILTERS, type DemolitionMoreFiltersType } from "./DemolitionList/MoreFiltersTypes"
import type { DemolitionParentStatus } from "./DemolitionService"

interface DemolitionListState {
    selectedParentStatus: DemolitionParentStatus
    searchKey: string
    statusIds: number[]
    // The whole option is kept, not just the id: the source site dropdown loads its options a
    // page at a time, so a selected site's title can't be looked up from a full client-side list.
    sourceSites: Option[]
    page: number
    perPage: number
    selectedTab: number
}

interface DemolitionListSliceState {
    visibleColumns: { name: string; visible: boolean }[]
    moreFilters: DemolitionMoreFiltersType
    demolitionListState: DemolitionListState
}

const initialState: DemolitionListSliceState = {
    visibleColumns: [
        { name: "Reason", visible: false },
        { name: "Last Updated On", visible: false },
        { name: "Raised By", visible: false },
        { name: "Executed By", visible: true },
        { name: "TAT", visible: false },
    ],
    moreFilters: INITIAL_MORE_FILTERS,
    demolitionListState: {
        selectedParentStatus: "all_tab",
        searchKey: "",
        statusIds: [],
        sourceSites: [],
        page: 1,
        perPage: 10,
        selectedTab: 0,
    },
}

const DemolitionListSlice = createSlice({
    name: "DemolitionList",
    initialState,
    reducers: {
        setDemolitionListVisibleColumns: (state, action: PayloadAction<{ name: string; visible: boolean }[]>) => {
            state.visibleColumns = action.payload
        },
        setDemolitionListMoreFilters: (state, action: PayloadAction<DemolitionMoreFiltersType>) => {
            state.moreFilters = action.payload
        },
        setDemolitionListParentStatus: (state, action: PayloadAction<DemolitionParentStatus>) => {
            state.demolitionListState.selectedParentStatus = action.payload
        },
        setDemolitionListSearchKey: (state, action: PayloadAction<string>) => {
            state.demolitionListState.searchKey = action.payload
        },
        setDemolitionListStatusIds: (state, action: PayloadAction<number[]>) => {
            state.demolitionListState.statusIds = action.payload
        },
        setDemolitionListSourceSites: (state, action: PayloadAction<Option[]>) => {
            state.demolitionListState.sourceSites = action.payload
        },
        setDemolitionListPage: (state, action: PayloadAction<number>) => {
            state.demolitionListState.page = action.payload
        },
        setDemolitionListPerPage: (state, action: PayloadAction<number>) => {
            state.demolitionListState.perPage = action.payload
        },
        setDemolitionListSelectedTab: (state, action: PayloadAction<number>) => {
            state.demolitionListState.selectedTab = action.payload
        },
    },
})

export const {
    setDemolitionListVisibleColumns,
    setDemolitionListMoreFilters,
    setDemolitionListParentStatus,
    setDemolitionListSearchKey,
    setDemolitionListStatusIds,
    setDemolitionListSourceSites,
    setDemolitionListPage,
    setDemolitionListPerPage,
    setDemolitionListSelectedTab,
} = DemolitionListSlice.actions

export default DemolitionListSlice.reducer
